import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TrackWeekSelector from "../components/TrackWeekSelector";
import { useRunStore } from "../state/runStore";
import { useTeamStore } from "../state/teamStore";
import { api } from "../utils/api";
import { formatFuelPerLap, formatSetupName } from "../utils/formatters";
import type {
  CommandCenterData,
  CommandCenterDriverActivity,
  CommandCenterRun,
  UploadFreshness,
} from "../types";

export default function CommandCenter() {
  const { teamId, seriesSlug, trackWeekId: routeTrackWeekId } = useParams<{ teamId: string; seriesSlug: string; trackWeekId: string }>();
  const { activeTeamId, activeTeam } = useTeamStore();
  const { selectedTrackWeekId, fetchTrackWeeks, trackWeeks } = useRunStore();
  const navigate = useNavigate();

  const [data, setData] = useState<CommandCenterData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (activeTeamId) {
      void fetchTrackWeeks(activeTeamId);
    }
  }, [activeTeamId, fetchTrackWeeks]);

  const fetchData = useCallback(async () => {
    if (!activeTeamId || !selectedTrackWeekId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api<CommandCenterData>(
        `/api/comparison/command-center?team_id=${activeTeamId}&track_week_id=${selectedTrackWeekId}`
      );
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load the command center.");
    } finally {
      setLoading(false);
    }
  }, [activeTeamId, selectedTrackWeekId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const activeWeek = useMemo(
    () => trackWeeks.find((tw) => tw.id === (routeTrackWeekId || selectedTrackWeekId)),
    [trackWeeks, routeTrackWeekId, selectedTrackWeekId]
  );

  const sortedDrivers = useMemo(() => {
    if (!data?.driverActivity) return [];
    return [...data.driverActivity].sort((a, b) => {
      if (a.bestLap == null && b.bestLap == null) return 0;
      if (a.bestLap == null) return 1;
      if (b.bestLap == null) return -1;
      return a.bestLap - b.bestLap;
    });
  }, [data]);

  if (!activeTeam) {
    return (
      <div className="page page--wide fade-in">
        <div className="empty-state">
          <div className="empty-state-title">No active team</div>
          <div className="empty-state-text">Choose or create a team to start using the weekly command center.</div>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="page page--wide fade-in">
        <div className="empty-state">
          <div className="spinner spinner-lg" />
          <div className="empty-state-title">Loading week overview</div>
          <div className="empty-state-text">Pulling the latest run summaries, driver activity, and runs awaiting review.</div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="page page--wide fade-in">
        <div className="empty-state">
          <div className="empty-state-title">Overview unavailable</div>
          <div className="empty-state-text">{error}</div>
        </div>
      </div>
    );
  }

  const isEmpty = !data || data.totalRuns === 0;

  // Eyebrow: sentence-case, "Week 12 · Darlington · Cup Series"
  const eyebrowParts: string[] = [];
  if (activeWeek?.weekNumber != null) eyebrowParts.push(`Week ${activeWeek.weekNumber}`);
  if (activeWeek?.trackName) eyebrowParts.push(activeWeek.trackName);
  if (activeWeek?.seriesName) eyebrowParts.push(stripSeasonSuffix(activeWeek.seriesName));
  else if (activeTeam.name) eyebrowParts.push(activeTeam.name);

  const leaderLap = sortedDrivers[0]?.bestLap ?? null;

  return (
    <div className="fade-in">
      {/* ============ EDITORIAL HEADER ============ */}
      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">{eyebrowParts.join("  ·  ") || "Race week"}</span>
          </div>
          <div className="editorial-meta-right">
            <FreshnessPill freshness={data?.uploadFreshness || null} />
            {!routeTrackWeekId && <TrackWeekSelector />}
          </div>
        </div>

        <h1 className="editorial-title">Command Center</h1>
      </header>

      {isEmpty ? (
        <div className="empty-state" style={{ minHeight: "16rem" }}>
          <div className="empty-state-title">No runs this week</div>
          <div className="empty-state-text">
            Upload a telemetry file to populate the overview, driver board, and comparison tools for this week.
          </div>
        </div>
      ) : (
        <>
          {/* ============ HERO METRIC + STAT STRIP ============ */}
          <div style={st.heroLayout}>
            <div className="bezel-shell bezel-shell--lg">
              <div className="bezel-core" style={st.heroBezel}>
                <div className="hero-metric-eyebrow">
                  <span className="eyebrow">Headline lap of the week</span>
                  {data?.bestShortRun?.runId && (
                    <span className="eyebrow" style={{ color: "var(--text-muted)" }}>
                      Run #{String(data.bestShortRun.runId).slice(-4)}
                    </span>
                  )}
                </div>

                <div className="hero-metric-value">
                  {data?.bestShortRun?.bestLapTime != null
                    ? formatLap(data.bestShortRun.bestLapTime)
                    : "--:--.---"}
                </div>

                <div className="hero-metric-context">
                  <div className="hero-metric-context-item">
                    <span className="hero-metric-context-label">Driver</span>
                    <span className="hero-metric-context-value" style={{ color: "var(--text-strong)" }}>
                      {data?.bestShortRun?.displayName || "—"}
                    </span>
                  </div>
                  <div className="hero-metric-context-item">
                    <span className="hero-metric-context-label">Setup</span>
                    <span className="hero-metric-context-value">
                      {formatSetupName(data?.bestShortRun?.setupName) || "—"}
                    </span>
                  </div>
                  <div className="hero-metric-context-item">
                    <span className="hero-metric-context-label">Green laps</span>
                    <span className="hero-metric-context-value">
                      {data?.bestShortRun?.greenLaps ?? 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div style={st.statStack}>
              <SoftStat label="Team runs" value={String(data?.totalRuns ?? 0)} />
              <SoftStat label="Green laps" value={String(data?.totalGreenLaps ?? 0)} />
              <SoftStat label="Drivers" value={String(data?.activeDriverCount ?? 0)} />
            </div>
          </div>

          {/* ============ RUN HIGHLIGHTS — asymmetric Bento ============ */}
          <SectionHead label="Run highlights" />

          <div style={st.bentoGrid} className="stagger">
            <WirePanelLarge
              title="Best long run"
              run={data?.bestLongRun || null}
              metric={data?.bestLongRun?.top10Avg != null ? formatLap(data.bestLongRun.top10Avg) : null}
              foot={data?.bestLongRun?.fuelPerLap != null ? formatFuelPerLap(data.bestLongRun.fuelPerLap, data.bestLongRun.weather?.units, 2) : null}
              emptyTitle="Need 20+ green laps"
              emptyCopy="A long-run leader appears after a fuller green-lap sample is uploaded."
              onOpen={data?.bestLongRun ? () => navigate(`/runs/${data.bestLongRun?.runId}`) : undefined}
            />
            <div style={st.bentoStack}>
              <WirePanel
                title="Best qualifying"
                run={data?.bestShortRun || null}
                metric={data?.bestShortRun?.bestLapTime != null ? formatLap(data.bestShortRun.bestLapTime) : null}
                foot={data?.bestShortRun ? `${data.bestShortRun.greenLaps ?? 0} green laps` : null}
                emptyTitle="No qualifying run yet"
                emptyCopy="Upload a qualifying setup to surface the fastest single-lap run."
                onOpen={data?.bestShortRun ? () => navigate(`/runs/${data.bestShortRun?.runId}`) : undefined}
              />
              <WirePanel
                title="Latest upload"
                run={data?.latestUpload || null}
                metric={data?.latestUpload?.createdAt ? formatRelativeTime(data.latestUpload.createdAt) : null}
                foot={data?.latestUpload?.bestLapTime != null ? `Best ${formatLap(data.latestUpload.bestLapTime)}` : null}
                emptyTitle="No uploads yet"
                emptyCopy="The most recent run will appear here once the team starts uploading this week."
                onOpen={data?.latestUpload ? () => navigate(`/runs/${data.latestUpload?.runId}`) : undefined}
              />
            </div>
          </div>

          {/* ============ DRIVER TOWER ============ */}
          <SectionHead
            label="Driver tower"
            aside={`${sortedDrivers.length} active`}
          />

          <div className="tower">
            <div className="tower-head" style={st.towerCols}>
              <span>Pos</span>
              <span>Driver</span>
              <span style={{ textAlign: "right" }}>Best lap</span>
              <span style={{ textAlign: "right" }}>Gap</span>
              <span style={{ textAlign: "right" }}>Runs</span>
              <span style={{ textAlign: "right" }}>Last</span>
            </div>
            {sortedDrivers.map((driver, idx) => (
              <TowerRow
                key={driver.userId}
                pos={idx + 1}
                driver={driver}
                leaderLap={leaderLap}
                onOpen={
                  teamId && seriesSlug && routeTrackWeekId
                    ? () => navigate(`/teams/${teamId}/series/${seriesSlug}/weeks/${routeTrackWeekId}/drivers?driver=${driver.userId}`)
                    : undefined
                }
              />
            ))}
          </div>

          {/* ============ NEEDS REVIEW ============ */}
          {data && data.needsReview.length > 0 && (
            <>
              <SectionHead
                label="Needs review"
                aside={`${data.needsReview.length} flagged`}
              />
              <div style={st.reviewList} className="stagger">
                {data.needsReview.map((item) => (
                  <button
                    key={item.runId}
                    type="button"
                    onClick={() => navigate(`/runs/${item.runId}`)}
                    className="alert-strip"
                  >
                    <span className="alert-strip-flag" />
                    <div className="alert-strip-body">
                      <div className="alert-strip-content">
                        <div className="alert-strip-title">{item.displayName || "Unknown driver"}</div>
                        <div className="alert-strip-sub">{formatSetupName(item.setupName)}</div>
                        <div className="alert-strip-reason">{item.reasons.join(" · ")}</div>
                      </div>
                      <span className="pill pill--warning">{item.greenLaps ?? 0} laps</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ============================================================
   SUB-COMPONENTS
   ============================================================ */

function SectionHead({ label, aside }: { label: string; aside?: string }) {
  return (
    <div className="section-head">
      <span className="section-label">{label}</span>
      {aside && <span className="section-aside" style={{ marginLeft: "auto", paddingLeft: "0.85rem" }}>{aside}</span>}
    </div>
  );
}

function SoftStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={st.softStat}>
      <span style={st.softStatLabel}>{label}</span>
      <span style={st.softStatValue}>{value}</span>
    </div>
  );
}

function WirePanel({
  title,
  run,
  metric,
  foot,
  emptyTitle,
  emptyCopy,
  onOpen,
}: {
  title: string;
  run: CommandCenterRun | null;
  metric: string | null;
  foot: string | null;
  emptyTitle: string;
  emptyCopy: string;
  onOpen?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className="wire-panel"
      style={{ minHeight: "9.5rem" }}
    >
      <div className="wire-panel-eyebrow">
        <span className="wire-panel-title">{title}</span>
      </div>

      {run ? (
        <>
          <div className="wire-panel-metric">{metric}</div>
          <div className="wire-panel-driver">{run.displayName || "Unknown driver"}</div>
          <div className="wire-panel-detail">{formatSetupName(run.setupName)}</div>
          {foot && <div className="wire-panel-foot">{foot}</div>}
        </>
      ) : (
        <>
          <div className="wire-panel-metric" style={{ fontSize: "1.15rem", color: "var(--text-default)" }}>{emptyTitle}</div>
          <div className="wire-panel-detail">{emptyCopy}</div>
        </>
      )}
    </button>
  );
}

function WirePanelLarge({
  title,
  run,
  metric,
  foot,
  emptyTitle,
  emptyCopy,
  onOpen,
}: {
  title: string;
  run: CommandCenterRun | null;
  metric: string | null;
  foot: string | null;
  emptyTitle: string;
  emptyCopy: string;
  onOpen?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className="wire-panel"
      style={st.wireLarge}
    >
      <div className="wire-panel-eyebrow">
        <span className="wire-panel-title">{title}</span>
      </div>

      {run ? (
        <>
          <div style={st.wireLargeMetric}>{metric}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", marginTop: "auto" }}>
            <div className="wire-panel-driver" style={{ fontSize: "1.15rem" }}>{run.displayName || "Unknown driver"}</div>
            <div className="wire-panel-detail">{formatSetupName(run.setupName)}</div>
          </div>
          {foot && <div className="wire-panel-foot">{foot}</div>}
        </>
      ) : (
        <>
          <div style={{ ...st.wireLargeMetric, fontSize: "1.6rem", color: "var(--text-default)" }}>{emptyTitle}</div>
          <div className="wire-panel-detail">{emptyCopy}</div>
        </>
      )}
    </button>
  );
}

function TowerRow({
  pos,
  driver,
  leaderLap,
  onOpen,
}: {
  pos: number;
  driver: CommandCenterDriverActivity;
  leaderLap: number | null;
  onOpen?: () => void;
}) {
  const gap = leaderLap != null && driver.bestLap != null && pos > 1
    ? `+${(driver.bestLap - leaderLap).toFixed(3)}`
    : "—";

  return (
    <button type="button" onClick={onOpen} disabled={!onOpen} className="tower-row" style={st.towerCols}>
      <span className="tower-pos">{String(pos).padStart(2, "0")}</span>
      <span style={{ display: "flex", flexDirection: "column", gap: "0.15rem", minWidth: 0 }}>
        <span className="tower-name">{driver.displayName || "Unknown driver"}</span>
        <span className="tower-mono tower-mono--faint" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {driver.bestSetup || "No setup"}
        </span>
      </span>
      <span className={`tower-mono tower-mono--lg ${pos === 1 ? "tower-mono--accent" : ""}`} style={{ textAlign: "right" }}>
        {driver.bestLap != null ? formatLap(driver.bestLap) : "—"}
      </span>
      <span className={`tower-mono ${pos === 1 ? "tower-mono--green" : "tower-mono--faint"}`} style={{ textAlign: "right" }}>
        {gap}
      </span>
      <span className="tower-mono tower-mono--faint" style={{ textAlign: "right" }}>
        {String(driver.runCount).padStart(2, "0")}
      </span>
      <span className="tower-mono tower-mono--faint" style={{ textAlign: "right" }}>
        {formatRelativeTime(driver.lastUploadDate)}
      </span>
    </button>
  );
}

function FreshnessPill({ freshness }: { freshness: UploadFreshness | null }) {
  if (!freshness) return null;
  // Editorial signal: dot carries the state color, pill stays quiet.
  // Live uploads get the only filled treatment (still a hint of accent).
  const dotClass =
    freshness.level === "stale" ? "flag-dot--red"
      : freshness.level === "aging" ? "flag-dot--yellow"
      : freshness.level === "live" ? "flag-dot--live"
      : "";
  const pillClass =
    freshness.level === "live" ? "pill pill--live" : "pill pill--quiet";

  return (
    <span className={pillClass}>
      <span className={`flag-dot ${dotClass}`} />
      {freshness.label}
    </span>
  );
}

/* ============================================================
   HELPERS
   ============================================================ */

function formatLap(time: number | null | undefined): string {
  if (time == null) return "--:--.---";
  const minutes = Math.floor(time / 60);
  const seconds = time - minutes * 60;
  return `${minutes}:${seconds.toFixed(3).padStart(6, "0")}`;
}

function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return "unknown";
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function stripSeasonSuffix(name: string): string {
  return name.replace(/\s*[-–]\s*\d{4}\s+Season\s*$/i, "").trim();
}

/* ============================================================
   STYLES
   ============================================================ */

const st: Record<string, CSSProperties> = {
  heroLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)",
    gap: "1rem",
    marginBottom: "0.5rem",
  },
  heroBezel: {
    padding: "1.75rem 1.85rem 1.6rem",
    minHeight: "16rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  statStack: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  softStat: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "0.85rem",
    padding: "1.1rem 1.25rem",
    border: "1px solid var(--rule)",
    borderRadius: "var(--r-md)",
    background: "var(--ink-2)",
  },
  softStatLabel: {
    fontFamily: "var(--font-body)",
    fontSize: "0.86rem",
    color: "var(--text-secondary)",
  },
  softStatValue: {
    fontFamily: "var(--font-mono)",
    fontSize: "1.5rem",
    fontWeight: 500,
    color: "var(--text-strong)",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
  },
  bentoGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.45fr) minmax(0, 1fr)",
    gap: "0.85rem",
  },
  bentoStack: {
    display: "flex",
    flexDirection: "column",
    gap: "0.85rem",
  },
  wireLarge: {
    minHeight: "20rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
    padding: "1.4rem 1.5rem 1.5rem",
  },
  wireLargeMetric: {
    fontFamily: "var(--font-display)",
    fontSize: "clamp(2.2rem, 4vw, 3rem)",
    fontWeight: 600,
    letterSpacing: "-0.05em",
    lineHeight: 1,
    color: "var(--text-strong)",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum" 1, "ss03" 1',
    marginTop: "0.25rem",
  },
  towerCols: {
    gridTemplateColumns: "3rem minmax(0, 1.6fr) minmax(7rem, 1fr) minmax(5rem, 0.8fr) minmax(3rem, 0.5fr) minmax(5rem, 0.7fr)",
    gap: "1rem",
  },
  reviewList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.6rem",
  },
};
